'use client';

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { usePathname } from 'next/navigation';
import { AnimatePresence, LayoutGroup } from 'motion/react';
import { useMediaQuery } from '@/hooks/use-media-query';
import {
  isEditableShortcutTarget,
  safeLocalStorageGetItem,
  safeLocalStorageRemoveItem,
  safeLocalStorageSetItem,
} from '@/lib/utils';
import {
  WALKTHROUGH_PAGES,
  type WalkthroughPage,
  type WalkthroughStep,
} from '@/components/walkthrough/walkthrough-steps';
import { WalkthroughOverlay } from '@/components/walkthrough/walkthrough-overlay';
import { WALKTHROUGHS_ENABLED } from '@/components/walkthrough/walkthrough-availability';
import { FloatingActionPill } from '@/components/floating-action-pill';

const STORAGE_PREFIX = 'walkthrough.';
const AUTO_START_DELAY = 800;
const AUTO_START_READY_POLL_INTERVAL = 120;
const AUTO_START_READY_MAX_WAIT = 4000;
const EXIT_ANIMATION_DELAY = 320;

function storageKey(pageKey: string) {
  return `${STORAGE_PREFIX}${pageKey}.completed`;
}

function getStepsForViewport(
  page: WalkthroughPage | null | undefined,
  isDesktopWalkthrough: boolean
) {
  if (!page) return [];
  const viewportSteps = isDesktopWalkthrough
    ? page.steps
    : (page.mobileSteps ?? page.steps);

  if (typeof document === 'undefined') {
    return viewportSteps;
  }

  return viewportSteps.filter((step) => {
    if (!step.includeIfSelectorExists) return true;
    return !!document.querySelector(step.includeIfSelectorExists);
  });
}

function getReadySelectorForViewport(
  page: WalkthroughPage | null | undefined,
  isDesktopWalkthrough: boolean
) {
  if (!page) return null;
  return isDesktopWalkthrough
    ? (page.readySelector ?? null)
    : (page.mobileReadySelector ?? page.readySelector ?? null);
}

function isWalkthroughPageReady(
  page: WalkthroughPage | null | undefined,
  isDesktopWalkthrough: boolean
) {
  const readySelector = getReadySelectorForViewport(page, isDesktopWalkthrough);
  if (!readySelector) return true;
  return typeof document !== 'undefined'
    ? !!document.querySelector(readySelector)
    : false;
}

type WalkthroughContextValue = {
  isActive: boolean;
  currentStepIndex: number;
  currentStep: WalkthroughStep | null;
  totalSteps: number;
  /**
   * True when the current route has a page walkthrough the user can launch
   * (or replay) on demand. Drives the floating help button so the tour is
   * discoverable — and reachable on mobile, where Shift+? is impossible.
   */
  hasWalkthroughForRoute: boolean;
  next: () => void;
  previous: () => void;
  skip: () => void;
  start: () => void;
  reset: () => void;
};

const WalkthroughContext = createContext<WalkthroughContextValue | null>(null);

export function useWalkthrough() {
  const ctx = useContext(WalkthroughContext);
  if (!ctx) {
    throw new Error('useWalkthrough must be used within WalkthroughProvider');
  }
  return ctx;
}

export function WalkthroughProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [isActive, setIsActive] = useState(false);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [activePageKey, setActivePageKey] = useState<string | null>(null);
  const cleanupTimeoutRef = useRef<number | null>(null);
  const isDesktopWalkthrough = useMediaQuery('(min-width: 1024px)');
  const pathname = usePathname();

  // Find which walkthrough page matches the current route
  const matchedPage = useMemo(() => {
    if (!WALKTHROUGHS_ENABLED || !pathname) return null;
    return (
      WALKTHROUGH_PAGES.find((page) =>
        new RegExp(page.routePattern).test(pathname)
      ) ?? null
    );
  }, [pathname]);

  const activePage = useMemo(
    () =>
      activePageKey
        ? (WALKTHROUGH_PAGES.find((p) => p.key === activePageKey) ?? null)
        : null,
    [activePageKey]
  );
  const steps = useMemo(
    () => getStepsForViewport(activePage, isDesktopWalkthrough),
    [activePage, isDesktopWalkthrough]
  );

  const clearPendingCleanup = useCallback(() => {
    if (cleanupTimeoutRef.current !== null) {
      window.clearTimeout(cleanupTimeoutRef.current);
      cleanupTimeoutRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => clearPendingCleanup();
  }, [clearPendingCleanup]);

  // Auto-start when navigating to a page with a walkthrough
  useEffect(() => {
    if (!WALKTHROUGHS_ENABLED) return;
    if (isActive || !matchedPage) return;
    if (matchedPage.autoStart === false) return;

    const completed = safeLocalStorageGetItem(storageKey(matchedPage.key));
    if (completed === 'true') return;

    let autoStartTimer: number | null = null;
    let readyPollInterval: number | null = null;
    let readyPollTimeout: number | null = null;

    const stopReadyPolling = () => {
      if (readyPollInterval !== null) {
        window.clearInterval(readyPollInterval);
        readyPollInterval = null;
      }
      if (readyPollTimeout !== null) {
        window.clearTimeout(readyPollTimeout);
        readyPollTimeout = null;
      }
    };

    const clearAutoStartTimers = () => {
      if (autoStartTimer !== null) {
        window.clearTimeout(autoStartTimer);
        autoStartTimer = null;
      }
      stopReadyPolling();
    };

    const tryScheduleAutoStart = () => {
      const pageSteps = getStepsForViewport(matchedPage, isDesktopWalkthrough);
      if (pageSteps.length === 0) return false;
      if (!isWalkthroughPageReady(matchedPage, isDesktopWalkthrough)) {
        return false;
      }

      if (autoStartTimer !== null) return true;

      autoStartTimer = window.setTimeout(() => {
        clearAutoStartTimers();
        clearPendingCleanup();
        setActivePageKey(matchedPage.key);
        setCurrentStepIndex(0);
        setIsActive(true);
      }, AUTO_START_DELAY);

      return true;
    };

    if (!tryScheduleAutoStart()) {
      readyPollInterval = window.setInterval(() => {
        if (tryScheduleAutoStart()) {
          stopReadyPolling();
        }
      }, AUTO_START_READY_POLL_INTERVAL);

      readyPollTimeout = window.setTimeout(() => {
        stopReadyPolling();
      }, AUTO_START_READY_MAX_WAIT);
    }

    return () => clearAutoStartTimers();
  }, [clearPendingCleanup, isActive, isDesktopWalkthrough, matchedPage]);

  // Shift+? keyboard shortcut to trigger walkthrough for current page
  // Skip if a dialog/popover is open (they handle Shift+? themselves)
  useEffect(() => {
    if (!WALKTHROUGHS_ENABLED) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.shiftKey && e.key === '?' && matchedPage) {
        if (isEditableShortcutTarget(e.target)) return;
        if (isActive) return;

        const hasOpenDialog = document.querySelector(
          '[role="dialog"][data-state="open"], [data-state="open"][data-radix-popper-content-wrapper]'
        );
        if (hasOpenDialog) return;

        const pageSteps = getStepsForViewport(
          matchedPage,
          isDesktopWalkthrough
        );
        if (pageSteps.length === 0) return;
        if (!isWalkthroughPageReady(matchedPage, isDesktopWalkthrough)) return;

        e.preventDefault();
        clearPendingCleanup();
        setActivePageKey(matchedPage.key);
        setCurrentStepIndex(0);
        setIsActive(true);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [clearPendingCleanup, isActive, isDesktopWalkthrough, matchedPage]);

  const close = useCallback(
    (markCompleted: boolean) => {
      if (markCompleted && activePageKey) {
        safeLocalStorageSetItem(storageKey(activePageKey), 'true');
      }
      clearPendingCleanup();
      setIsActive(false);
      cleanupTimeoutRef.current = window.setTimeout(() => {
        setCurrentStepIndex(0);
        setActivePageKey(null);
        cleanupTimeoutRef.current = null;
      }, EXIT_ANIMATION_DELAY);
    },
    [activePageKey, clearPendingCleanup]
  );

  const next = useCallback(() => {
    if (currentStepIndex >= steps.length - 1) {
      close(true);
    } else {
      setCurrentStepIndex((i) => i + 1);
    }
  }, [close, currentStepIndex, steps.length]);

  const previous = useCallback(() => {
    setCurrentStepIndex((i) => Math.max(0, i - 1));
  }, []);

  const skip = useCallback(() => {
    close(true);
  }, [close]);

  const start = useCallback(() => {
    if (!WALKTHROUGHS_ENABLED) return;
    if (matchedPage) {
      const pageSteps = getStepsForViewport(matchedPage, isDesktopWalkthrough);
      if (pageSteps.length === 0) return;
      if (!isWalkthroughPageReady(matchedPage, isDesktopWalkthrough)) return;

      clearPendingCleanup();
      setActivePageKey(matchedPage.key);
      setCurrentStepIndex(0);
      setIsActive(true);
    }
  }, [clearPendingCleanup, isDesktopWalkthrough, matchedPage]);

  const reset = useCallback(() => {
    clearPendingCleanup();
    for (const page of WALKTHROUGH_PAGES) {
      safeLocalStorageRemoveItem(storageKey(page.key));
    }
    setCurrentStepIndex(0);
    setIsActive(false);
    setActivePageKey(null);
  }, [clearPendingCleanup]);

  const currentStep =
    currentStepIndex < steps.length ? (steps[currentStepIndex] ?? null) : null;

  // Does the current route ship a walkthrough the user could launch? Counts
  // the static viewport step list (ignores `includeIfSelectorExists` DOM
  // checks so the answer is stable across renders); `start()` still guards
  // readiness when actually invoked.
  const hasWalkthroughForRoute = useMemo(() => {
    if (!WALKTHROUGHS_ENABLED) return false;
    if (!matchedPage) return false;
    const viewportSteps = isDesktopWalkthrough
      ? matchedPage.steps
      : (matchedPage.mobileSteps ?? matchedPage.steps);
    return viewportSteps.length > 0;
  }, [matchedPage, isDesktopWalkthrough]);

  const value = useMemo<WalkthroughContextValue>(
    () => ({
      isActive,
      currentStepIndex,
      currentStep,
      totalSteps: steps.length,
      hasWalkthroughForRoute,
      next,
      previous,
      skip,
      start,
      reset,
    }),
    [
      isActive,
      currentStepIndex,
      currentStep,
      steps.length,
      hasWalkthroughForRoute,
      next,
      previous,
      skip,
      start,
      reset,
    ]
  );

  return (
    <WalkthroughContext.Provider value={value}>
      <LayoutGroup id="walkthrough">
        {children}
        <FloatingActionPill />
        <AnimatePresence>
          {WALKTHROUGHS_ENABLED && isActive ? <WalkthroughOverlay /> : null}
        </AnimatePresence>
      </LayoutGroup>
    </WalkthroughContext.Provider>
  );
}
